import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { useDragResize } from '../../hooks/useDragResize'
import {
  DEFAULT_ATTACHMENT_BAR_WIDTH,
  registerAttachmentBar,
  unregisterAttachmentBar,
  updateAttachmentBar,
} from '../../utils/attachmentBarPresence'
import * as S from './AttachmentBar.styles'
import { AttachmentBarProps, AttachmentBarRef } from './AttachmentBar.types'
import { SidePanelContentModeToggle } from './SidePanelContentModeToggle'

const DEFAULT_RESIZE_MAX_WIDTH = 960

interface AttachmentContentProps extends AttachmentBarProps {
  hasTransitionAnimation: boolean
  rightOffset?: number
  screenHasSecondaryMenu?: boolean
  width?: number
  shouldHideHeader?: boolean
}

const AttachmentBarContent = forwardRef<AttachmentBarRef, AttachmentContentProps>(
  (
    {
      title,
      children,
      filter,
      footer,
      closeOnOutsideClick,
      onClose,
      hasTransitionAnimation,
      resizable = false,
      resizeMinWidth,
      resizeMaxWidth,
      resizeStorageKey,
      rightOffset,
      screenHasSecondaryMenu,
      shouldOverlayContent = false,
      style,
      width,
      shouldHideHeader = false,
    },
    ref,
  ) => {
    const [unmounting, setUnmounting] = useState(false)
    const hasFilter = Boolean(filter)

    const internalRef = useRef<HTMLDivElement>(null)

    // Drag the panel's left edge to widen it (opt-in via `resizable`). The
    // container is sized imperatively while dragging — no re-render per frame;
    // the committed size then feeds the width prop and presence registration.
    const {
      size: draggedWidth,
      isResizing,
      handleProps: resizeHandleProps,
    } = useDragResize({
      axis: 'x',
      min: resizeMinWidth ?? DEFAULT_ATTACHMENT_BAR_WIDTH,
      max: resizeMaxWidth ?? DEFAULT_RESIZE_MAX_WIDTH,
      storageKey: resizeStorageKey,
      getTarget: () => internalRef.current,
    })
    const effectiveWidth = resizable ? (draggedWidth ?? width) : width

    // Let app layouts know a panel is open and how much right-edge space it
    // occupies (width + tiling offset), so 'push' content mode can clear it —
    // including panels mounted directly, outside any side panel store.
    const occupiedRightWidth = (effectiveWidth ?? DEFAULT_ATTACHMENT_BAR_WIDTH) + (rightOffset ?? 0)
    const presenceIdRef = useRef<number | null>(null)

    useEffect(() => {
      if (shouldOverlayContent) return
      presenceIdRef.current = registerAttachmentBar(occupiedRightWidth)
      return () => {
        if (presenceIdRef.current !== null) unregisterAttachmentBar(presenceIdRef.current)
        presenceIdRef.current = null
      }
      // Overlay panels do not reserve page space; size changes use the update effect.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldOverlayContent])

    useEffect(() => {
      if (presenceIdRef.current !== null)
        updateAttachmentBar(presenceIdRef.current, occupiedRightWidth)
    }, [occupiedRightWidth])

    const handleClose = () => {
      setUnmounting(true)
      setTimeout(() => {
        onClose?.()
      }, 250)
    }

    useImperativeHandle(ref, () => ({
      close: handleClose,
    }))

    return (
      <>
        {closeOnOutsideClick && <S.OutsideContainer onClick={handleClose} />}

        <S.Container
          ref={internalRef}
          unmounting={unmounting}
          hasTransitionAnimation={hasTransitionAnimation}
          rightOffset={rightOffset}
          screenHasSecondaryMenu={screenHasSecondaryMenu}
          style={style}
          width={effectiveWidth}
        >
          {resizable && (
            <S.ResizeHandle
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel"
              $active={isResizing}
              {...resizeHandleProps}
            />
          )}
          {!shouldHideHeader && (
            <S.Header hasFilter={hasFilter}>
              {title && <S.Title>{title}</S.Title>}
              {hasFilter && <S.FilterContainer>{filter}</S.FilterContainer>}
              {!shouldOverlayContent && <SidePanelContentModeToggle />}
              <S.CloseButton onClick={handleClose} data-cy="attachment-bar-close-button" />
            </S.Header>
          )}
          {children}
          {footer && <S.Footer width={effectiveWidth}>{footer}</S.Footer>}
        </S.Container>
      </>
    )
  },
)

export { AttachmentBarContent }
